import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function PlatformStaffInvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { acceptStaffInvite } = useAuth();
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/onboard/staff/${token}`)
            .then((r) => setInvite(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "This invitation is invalid or has expired."));
    }, [token]);

    const submit = async () => {
        if (password.length < 8) return toast.error("Password must be at least 8 characters.");
        if (password !== confirm) return toast.error("Passwords don't match.");
        setSaving(true);
        try {
            await acceptStaffInvite(token, password);
            toast.success("Welcome to Blue Phoenix!");
            navigate("/admin", { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not complete setup.");
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="platform-staff-invite-page">
            <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full space-y-5">
                <div className="text-center space-y-2">
                    <span className="w-14 h-14 mx-auto rounded-2xl bg-phoenix-navy text-white flex items-center justify-center"><ShieldCheck className="w-7 h-7" /></span>
                    <h1 className="font-display font-bold text-2xl text-phoenix-navy">Join the Blue Phoenix team</h1>
                </div>

                {error ? (
                    <div data-testid="staff-invite-error" className="px-4 py-3 bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-xl text-center">{error}</div>
                ) : !invite ? (
                    <div className="py-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
                ) : (
                    <>
                        <div className="text-sm text-center text-muted-foreground">
                            <span className="font-semibold text-phoenix-navy">{invite.full_name}</span> · {invite.email}
                            <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-phoenix-navy bg-phoenix-soft px-2.5 py-1 rounded">
                                <ShieldCheck className="w-3 h-3" /> {invite.admin_scope} role
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div className="relative">
                                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input data-testid="staff-invite-password" type="password" className="w-full pl-9 pr-3 py-2.5 bg-white border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30" placeholder="Create a password (8+ chars)" value={password} onChange={(e) => setPassword(e.target.value)} />
                            </div>
                            <div className="relative">
                                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input data-testid="staff-invite-confirm" type="password" className="w-full pl-9 pr-3 py-2.5 bg-white border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                            </div>
                        </div>
                        <button onClick={submit} disabled={saving} data-testid="staff-invite-accept" className="w-full py-3 bg-phoenix-navy text-white font-semibold rounded-xl hover:bg-phoenix-navy-2 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Set password &amp; continue
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
